import React, { useEffect, useState } from 'react';
import { fetchChatContacts } from '../../services/chatContacts';
import { useAuth } from '../../contexts/AuthContext';
import socketClient from '../../utils/socketClient';
import LogoLoader from '../../components/Common/LogoLoader';

// Floating chat contacts bubble listing finalized offer counterparties
const ChatContactsBubble = ({ onOpenChat }) => {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState(null);
  const formatAmount = (amount) => `${new Intl.NumberFormat('en-US').format(amount ?? 0)} TND`;

  const loadContacts = async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchChatContacts();
      setContacts(data);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContacts();
  const socket = socketClient.socket || null;
    if (socket) {
      const refreshEvents = ['offer_accepted', 'chat_started'];
      refreshEvents.forEach(ev => socket.on(ev, loadContacts));
      return () => refreshEvents.forEach(ev => socket.off(ev, loadContacts));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (!isAuthenticated) return null;

  return (
    <div className="fixed bottom-6 right-6 z-40">
      {open && (
        <div className="mb-3 w-72 bg-white/95 dark:bg-gray-900/95 backdrop-blur border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-4 max-h-96 overflow-y-auto">
          <div className="flex justify-between items-center mb-3">
            <h4 className="text-gray-900 dark:text-white font-semibold text-sm">Chat Contacts</h4>
            <button onClick={() => loadContacts()} className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">⟳</button>
          </div>
          {loading && <LogoLoader size={40} />}
          {error && <div className="text-red-500 dark:text-red-400 text-xs">{error}</div>}
          {!loading && !error && contacts.length === 0 && (
            <div className="text-gray-500 text-xs">No contacts yet.</div>
          )}
          <ul className="space-y-2">
            {contacts.map(c => (
              <li key={c.userId} className="bg-gray-100 dark:bg-gray-800/70 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors rounded-lg p-2">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-gray-900 dark:text-white text-sm font-medium">{c.username}</p>
                    <p className="text-gray-500 dark:text-gray-400 text-[11px] truncate">{c.email}</p>
                    {c.offers && c.offers[0] && (
                      <p className="text-[10px] text-gray-500 mt-0.5">Last: {formatAmount(c.offers[0].offerAmount)}</p>
                    )}
                  </div>
                  <button
                    onClick={() => onOpenChat && onOpenChat(c)}
                    className="ml-2 px-2 py-1 bg-blue-600 hover:bg-blue-500 rounded text-white text-xs"
                  >Chat</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-lg flex items-center justify-center text-white text-2xl focus:outline-none focus:ring-4 focus:ring-blue-400/40"
        aria-label="Chat Contacts"
      >{open ? '×' : '💬'}</button>
    </div>
  );
};

export default ChatContactsBubble;
